import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

/**
 * Reusable Contact-Sales dialog used across all 5 modules.
 *
 * Props:
 *   open, onOpenChange     — controlled dialog state
 *   defaultModule         — pre-selected module (one of MODULES below)
 *   apiClient             — optional axios instance; falls back to a public client
 *   user                  — optional logged-in user (to pre-fill name/email/mobile)
 */
export const SALES_MODULES = [
  "IPO Readiness Assessment",
  "DRHP Builder",
  "IPO Funding",
  "Match Making",
  "Business Valuation",
];

const ContactSalesDialog = ({
  open,
  onOpenChange,
  defaultModule = "IPO Readiness Assessment",
  apiClient,
  user,
}) => {
  const initial = {
    full_name: user?.full_name || "",
    email: user?.email || "",
    mobile: user?.mobile || "",
    module: defaultModule,
    message: "",
  };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm({ ...initial, module: defaultModule });
    setSubmitted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = form.full_name.trim();
    const email = form.email.trim();
    const mobile = form.mobile.trim();
    if (!name || !email || !mobile) {
      toast.error("Please fill in name, email and mobile");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const client =
        apiClient ||
        axios.create({
          baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
          headers: { "Content-Type": "application/json" },
        });
      await client.post("/contact/lead", {
        full_name: name,
        email,
        mobile,
        module: form.module,
        query: form.message || `Contact-sales enquiry for ${form.module}.`,
        lead_type: "sales",
      });
      setSubmitted(true);
      toast.success("Thanks! Our team will get back within one working day.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange?.(o);
        if (!o) setTimeout(reset, 250);
      }}
    >
      <DialogContent className="sm:max-w-lg" data-testid="contact-sales-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#003366]">
            <Mail className="w-4 h-4" />
            Talk to our Sales team
          </DialogTitle>
          <DialogDescription>
            Tell us a bit about you and which module you're interested in — we'll respond within one working day.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-6 flex flex-col items-center text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-3" />
            <p className="font-semibold text-gray-800">Request received</p>
            <p className="text-xs text-gray-500 mt-1 max-w-xs">
              We've logged your interest in <span className="font-semibold">{form.module}</span> and our team will reach out shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3 pt-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Full name *</Label>
                <Input value={form.full_name} onChange={setField("full_name")} placeholder="Your name" className="h-9 text-sm mt-1" data-testid="cs-name" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Mobile *</Label>
                <Input value={form.mobile} onChange={setField("mobile")} placeholder="+91 9xxxxxxxxx" className="h-9 text-sm mt-1" data-testid="cs-mobile" />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Email *</Label>
              <Input type="email" value={form.email} onChange={setField("email")} placeholder="you@company.com" className="h-9 text-sm mt-1" data-testid="cs-email" />
            </div>

            <div>
              <Label className="text-xs font-semibold">Which module are you interested in? *</Label>
              <select
                value={form.module}
                onChange={setField("module")}
                className="w-full mt-1 h-9 px-3 text-sm border border-input rounded-md bg-background"
                data-testid="cs-module"
              >
                {SALES_MODULES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs font-semibold">Anything else? <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Textarea
                value={form.message}
                onChange={setField("message")}
                placeholder="Tell us about your IPO timeline, team size, or specific questions…"
                rows={3}
                className="text-sm mt-1"
                data-testid="cs-message"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0 pt-1">
              <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#003366] hover:bg-[#002244] text-white gap-2"
                data-testid="cs-submit"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit enquiry
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactSalesDialog;
